import nodeQuery from '@aurora/shared-client/components/context/ContextNode.query.graphql';
import InputEditForm from '@aurora/shared-client/components/form/InputEditForm/InputEditForm';
import type { FormSpec } from '@aurora/shared-client/components/form/types';
import useQueryWithTracing from '@aurora/shared-client/components/useQueryWithTracing';
import FormBuilder from '@aurora/shared-client/helpers/form/FormBuilder/FormBuilder';
import type { SearchResultsPageAndParams } from '@aurora/shared-client/routes/endUserRoutes';
import useEndUserRoutes from '@aurora/shared-client/routes/useEndUserRoutes';
import type {
  ContextNodeQuery,
  ContextNodeQueryVariables
} from '@aurora/shared-generated/types/graphql-types';
import {
  EndUserComponent,
  EndUserPages,
  EndUserQueryParams
} from '@aurora/shared-types/pages/enums';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import type { ContextUserQuery, ContextUserQueryVariables } from '../../../types/graphql-types';
import type { WidgetFC, WidgetProps } from '../../common/Widget/types';
import userQuery from '../../context/ContextUser.query.graphql';
import messageSearchViewStyles from '../../messages/MessageSearchView/MessageSearchViewInline/MessageSearchViewInline.module.pcss';
import useTranslation from '../../useTranslation';
import SERPWidgetForm from './SearchResultPageForm.form.json';
import localStyles from './SearchWidget.module.pcss';
import SearchWidgetFilters from './SearchWidgetFilters/SearchWidgetFilters';
import type { Form } from '@aurora/shared-generated/types/graphql-schema-types';
/**
 * Form input field
 */
interface SearchData {
  /**
   * Search input.
   */
  search: string;
}

interface Props extends WidgetProps {
  /**
   * Class name(s) to apply to the wrapping element.
   */
  className?: string;
}

/**
 * Renders search widget
 *
 * @constructor
 * @author Amit Agrawal, Kiran Kumari
 */
const SearchWidget: WidgetFC<Props> = ({ className }) => {
  const cx = useClassNameMapper(localStyles);
  const messageSearchViewCx = useClassNameMapper(messageSearchViewStyles);
  const i18n = useTranslation(EndUserComponent.SEARCH_WIDGET);
  const { loading: textLoading } = i18n;
  const { router } = useEndUserRoutes();
  const searchTerm = router.getUnwrappedQueryParam(EndUserQueryParams.SEARCH_KEY);
  const lastUpdateFilter = router.getUnwrappedQueryParam(
    EndUserQueryParams.SEARCH_FILTER_BY_LAST_UPDATE
  );
  const contentTypeFilter = router.getUnwrappedQueryParam(
    EndUserQueryParams.SEARCH_FILTER_BY_CONTENT_TYPE
  );
  const locationFilter = router.getUnwrappedQueryParam(
    EndUserQueryParams.SEARCH_FILTER_BY_LOCATION
  );
  const repliesFilter = router.getUnwrappedQueryParam(EndUserQueryParams.SEARCH_FILTER_BY_REPLIES);
  const authorFilter = router.getUnwrappedQueryParam(EndUserQueryParams.SEARCH_FILTER_BY_AUTHOR);
  const searchOnlyInRootPostsFilter = router.getUnwrappedQueryParam(
    EndUserQueryParams.SEARCH_ONLY_IN_TOPICS
  );

  const [queryTerm, setQueryTerm] = useState<string>(searchTerm);
  const emptyResults = useRef<string[]>([]);
  const loadingResultsCount = useRef<number>(0);
  const filterCount = useRef<number>(0);
  const primaryResultReference = useRef<HTMLDivElement>(null);
  const secondaryResultReference = useRef<HTMLDivElement>(null);
  const [highlightResult, setHighlightResult] = useState<boolean>(true);
  const highlightedContentClass = cx('lia-highlighted-content');
  const primaryResultsCss = cx('lia-item-link');
  const secondaryResultsCss = cx('lia-user-content-box');
  const searchInlineCss = messageSearchViewCx('lia-search-inline');

  if (
    lastUpdateFilter ||
    contentTypeFilter ||
    locationFilter ||
    repliesFilter ||
    authorFilter ||
    searchOnlyInRootPostsFilter
  ) {
    filterCount.current += 1;
  }

  /**
   * callback to add the highlight for content
   */
  const addContentHighlight = useCallback(
    (items, index) => {
      items[index]?.classList.add(highlightedContentClass);
    },
    [highlightedContentClass]
  );

  /**
   * function to remove highlight for content
   *
   * @param items item list
   * @param index the index where highlight need to be removed
   */
  const removeContentHighlight = (items, index) => {
    items[index]?.classList.remove(highlightedContentClass);
  };

  /**
   * Function to get the active element in primary and secondary results
   */
  const getActiveChild = (): Element => {
    const focusSection = highlightResult
      ? primaryResultReference.current
      : secondaryResultReference.current;
    if (!focusSection) {
      return;
    }
    return focusSection.querySelector(`.${highlightedContentClass}`);
  };

  /**
   * Function to clean the highlight
   */
  const cleanContentHighlight = () => {
    const active = getActiveChild();
    active?.classList.remove(highlightedContentClass);
  };

  /**
   * Function to focus on the active element
   *
   * @param linkItemCss css of the section that needs to be focused
   */
  const focusResults = useCallback(
    (): void => {
      const linkItemCss = highlightResult ? primaryResultsCss : secondaryResultsCss;
      const activeLink: HTMLElement = getActiveChild()?.querySelector(
        `.${linkItemCss}`
      ) as HTMLElement;
      if (activeLink) {
        activeLink.focus();
        activeLink.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [highlightResult]
  );

  /**
   * Function to handle the up and down arrow keys
   *
   * @param offset the offset value for up and down arrow keys
   */
  function handleNextActiveChild(offset: number): void {
    let activeParentElement;
    const focusSection = highlightResult
      ? primaryResultReference.current
      : secondaryResultReference.current;
    const items = highlightResult
      ? [...focusSection.querySelectorAll(`li div.${searchInlineCss}`)]
      : [...focusSection.querySelectorAll('li')];
    const activeElement = document.activeElement.closest('li');
    const activeHighlightedElement = focusSection.querySelector(
      `.${highlightedContentClass}`
    ) as HTMLLIElement;
    if (highlightResult) {
      activeParentElement = activeElement?.firstElementChild;
    } else {
      activeParentElement = activeElement;
    }
    const currentIndexOfHighlightedElement = [...items].indexOf(activeHighlightedElement);
    let currentIndexOfActiveElement = [...items].indexOf(activeParentElement);
    if (currentIndexOfActiveElement === -1) {
      currentIndexOfActiveElement = currentIndexOfHighlightedElement;
    }
    let nextIndex = currentIndexOfActiveElement + offset;
    if (nextIndex > items.length) {
      nextIndex = 0;
    }
    if (items[nextIndex]) {
      removeContentHighlight(items, currentIndexOfHighlightedElement);
      addContentHighlight(items, nextIndex);
    }
    focusResults();
  }

  /**
   * function to handle the key down event
   *
   * @param event for the keyboard event
   */
  function handleKeyDown(event): void {
    switch (event.key) {
      case 'ArrowUp': {
        handleNextActiveChild(-1);
        break;
      }
      case 'ArrowDown': {
        handleNextActiveChild(1);
        break;
      }
      case 'ArrowRight': {
        if (highlightResult) {
          cleanContentHighlight();
          setHighlightResult(false);
        }
        break;
      }
      case 'ArrowLeft': {
        if (!highlightResult) {
          cleanContentHighlight();
          setHighlightResult(true);
        }
        break;
      }
      case 'Tab': {
        cleanContentHighlight();
        break;
      }
      default: {
        return;
      }
    }
  }

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('click', () => {
      cleanContentHighlight();
      window.removeEventListener('keydown', handleKeyDown);
    });
    return (): void => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  });

  useEffect(() => {
    const focusSection = highlightResult
      ? primaryResultReference.current
      : secondaryResultReference.current;
    if (!focusSection) {
      return;
    }
    const items = highlightResult
      ? [...focusSection?.querySelectorAll(`li div.${searchInlineCss}`)]
      : [...focusSection?.querySelectorAll('li')];
    if (items) {
      addContentHighlight(items, 0);
    }
    focusResults();
  }, [highlightResult, addContentHighlight, focusResults, searchInlineCss]);

  useEffect(() => {
    if (queryTerm !== searchTerm) {
      setQueryTerm(searchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const { data } = useQueryWithTracing<ContextNodeQuery, ContextNodeQueryVariables>(
    module,
    nodeQuery,
    {
      variables: {
        id: locationFilter
      },
      skip: !locationFilter
    }
  );

  const { data: contextUserData } = useQueryWithTracing<
    ContextUserQuery,
    ContextUserQueryVariables
  >(module, userQuery, {
    variables: {
      id: `user:${authorFilter}`
    },
    skip: !authorFilter
  });

  if (textLoading) {
    return null;
  }

  const formSpecs: FormSpec<SearchData> = new FormBuilder<SearchData>(
    'SearchResultPageForm',
    i18n,
    {
      schema: SERPWidgetForm as Form,
      cx
    }
  )
    .addSearchField({
      name: 'search',
      size: 'lg',
      className: cx('lia-search-field'),
      formGroupSpec: {
        className: cx('lia-g-mb-0'),
        label: false
      },
      defaultValue: queryTerm ?? searchTerm ?? ''
    })
    .build();

  return (
    <div className={cx(className)}>
      <InputEditForm<SearchData>
        formSpec={formSpecs}
        onSubmit={async (
          formData: SearchData,
          _,
          event: React.FormEvent<HTMLFormElement>
        ): Promise<void> => {
          const { search } = formData;
          const trimmedSearch = search.trim();
          if (event && trimmedSearch.length > 1) {
            setQueryTerm(trimmedSearch);
            emptyResults.current = [];
            loadingResultsCount.current = 0;
            filterCount.current = 0;
            const existingQueryParams = router.getQueryParams();
            await router.replaceRoute<SearchResultsPageAndParams>(
              EndUserPages.SearchResultsPage,
              null,
              {
                ...existingQueryParams,
                [EndUserQueryParams.SEARCH_KEY]: trimmedSearch
              },
              { shallow: true }
            );
          }
        }}
        submitOnChange={{
          watchFields: ['search']
        }}
      />
      {queryTerm && (
        <SearchWidgetFilters
          contextNode={data?.coreNode}
          author={contextUserData?.user}
          onChange={() => {
            emptyResults.current = [];
            loadingResultsCount.current = 0;
            filterCount.current += 1;
          }}
        />
      )}
    </div>
  );
};

export default SearchWidget;
